<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);

const dialogRef = ref(null);
const fileInput = ref(null);

const hasSelectedFile = ref(null);
const selectedFileName = ref('');
const importProgress = ref(0);
const importStatus = ref('idle'); // idle, uploading, processing, background, success, error
const isBackgroundProcessing = ref(false);
const importMessage = ref('');

const handleFileClick = () => fileInput.value?.click();

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);

  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';
  importStatus.value = 'idle';
  importProgress.value = 0;
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  selectedFileName.value = '';
  importStatus.value = 'idle';
  importProgress.value = 0;
};

const resetImport = () => {
  handleRemoveFile();
  importStatus.value = 'idle';
  importProgress.value = 0;
  importMessage.value = '';
  isBackgroundProcessing.value = false;
};

const setBackgroundProcessing = message => {
  importStatus.value = 'background';
  isBackgroundProcessing.value = true;
  importMessage.value = message || 'Procesando en segundo plano...';
  importProgress.value = 100;
};

const uploadFile = async () => {
  if (!hasSelectedFile.value) return;

  importStatus.value = 'uploading';
  importProgress.value = 30;
  importMessage.value = t(
    'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.UPLOADING'
  );

  try {
    await emit('import', hasSelectedFile.value);
    // El padre maneja el cierre del diálogo según la respuesta
    // Si es importación sincrónica (<1000), el padre cierra
    // Si es background, el padre muestra la barra indeterminada
  } catch (error) {
    importStatus.value = 'error';
    importMessage.value =
      error.message ||
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.ERROR_MESSAGE');
  }
};

const setSuccess = () => {
  importProgress.value = 100;
  importStatus.value = 'success';
  importMessage.value = t(
    'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.SUCCESS_MESSAGE'
  );

  // Close dialog after success
  setTimeout(() => {
    dialogRef.value?.close();
    resetImport();
  }, 1000);
};

defineExpose({ dialogRef, resetImport, setBackgroundProcessing, setSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.TITLE')"
    :confirm-button-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.IMPORT')
    "
    :is-loading="isImportingContact"
    :disable-confirm-button="isImportingContact"
    @confirm="uploadFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.DESCRIPTION') }}
      </p>
    </template>

    <div class="flex flex-col gap-4">
      <!-- File Selection Section -->
      <div v-if="importStatus === 'idle'" class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <label class="text-sm text-n-slate-12 whitespace-nowrap">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.LABEL') }}
          </label>
          <div class="flex items-center justify-between w-full gap-2">
            <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
              {{ selectedFileName }}
            </span>
            <Button
              v-if="!hasSelectedFile"
              :label="
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CHOOSE_FILE')
              "
              icon="i-lucide-upload"
              color="slate"
              variant="ghost"
              size="sm"
              class="!w-fit"
              @click="handleFileClick"
            />
            <div v-else class="flex items-center gap-1">
              <Button
                :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CHANGE')"
                color="slate"
                variant="ghost"
                size="sm"
                @click="handleFileClick"
              />
              <div class="w-px h-3 bg-n-strong" />
              <Button
                icon="i-lucide-trash"
                color="slate"
                variant="ghost"
                size="sm"
                @click="handleRemoveFile"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Progress Section -->
      <div v-else class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ importMessage }}
          </span>
          <span
            v-if="importStatus !== 'background'"
            class="text-xs text-n-slate-11"
          >
            {{ importProgress }}<span>{{ t('COMMON.PERCENT') }}</span></span
          >
          <span v-else class="text-xs text-n-slate-11 flex items-center gap-1">
            <span class="w-2 h-2 bg-n-brand rounded-full animate-pulse" />
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.IN_PROGRESS') }}
          </span>
        </div>
        <div class="w-full h-2 bg-n-slate-3 rounded-full overflow-hidden">
          <div
            v-if="importStatus !== 'background'"
            class="h-full bg-n-brand transition-all duration-300"
            :style="{ width: `${importProgress}%` }"
          />
          <!-- Indeterminate progress bar for background processing -->
          <div v-else class="h-full bg-n-brand w-1/2 animate-pulse" />
        </div>
        <p
          v-if="importStatus === 'background'"
          class="text-xs text-n-slate-10 mt-1"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.BACKGROUND_NOTICE')
          }}
        </p>
      </div>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
